import React from 'react';
import MessageContentRenderer from './MessageContentRenderer';
import CollapsibleCodeBlock from './CollapsibleCodeBlock';

interface DataAnalysisResponseProps {
  content: string;
  isStreaming: boolean;
  debugInfo?: any;
  onContentLoaded?: () => void;
  translationData?: any;
  modelId?: string | null;
}

// Regex to match completed markdown code fences (```lang\n...code...\n```)
const CODE_BLOCK_REGEX = /```[^\n`]*\r?\n[\s\S]*?```/g;
// Regex to detect an unclosed code fence (opened but not yet closed during streaming)
const UNCLOSED_FENCE_REGEX = /```[^\n`]*\r?\n[\s\S]*$/;

/**
 * Splits a data analysis response into text content (always visible)
 * and code content (grouped in a single collapsible block).
 */
const DataAnalysisResponse: React.FC<DataAnalysisResponseProps> = ({
  content,
  isStreaming,
  debugInfo,
  onContentLoaded,
  translationData,
  modelId,
}) => {
  const { textContent, codeContent } = React.useMemo(() => {
    if (typeof content !== 'string') {
      return { textContent: content, codeContent: '' };
    }

    // Extract all completed code blocks, preserving their original language tag
    const codeBlocks = content.match(CODE_BLOCK_REGEX) || [];
    // Each entry: { lang, code } — lang is the original fence tag (e.g. "python", "json", "")
    const codeParts = codeBlocks.map(block => {
      const langMatch = block.match(/^```([^\n`]*)[ \t]*\n?/);
      const lang = (langMatch?.[1] ?? '').trim();
      const code = block.replace(/^```[^\n`]*[ \t]*\n?/, '').replace(/\n?```$/, '');
      return { lang, code };
    });

    // During streaming, also capture any unclosed code fence (still being written)
    let remainingContent = content.replace(CODE_BLOCK_REGEX, '');
    if (isStreaming) {
      const unclosedMatch = remainingContent.match(UNCLOSED_FENCE_REGEX);
      if (unclosedMatch) {
        const langMatch = unclosedMatch[0].match(/^```([^\n`]*)[ \t]*\n?/);
        const lang = (langMatch?.[1] ?? '').trim();
        const unclosedCode = unclosedMatch[0].replace(/^```[^\n`]*[ \t]*\n?/, '');
        if (unclosedCode.trim()) {
          codeParts.push({ lang, code: unclosedCode });
        }
        remainingContent = remainingContent.replace(UNCLOSED_FENCE_REGEX, '');
      }
    }

    const codeContent = codeParts.length > 0
      ? codeParts.map(({ lang, code }) => `\`\`\`${lang}\n${code}\n\`\`\``).join('\n\n')
      : '';

    // Clean up extra whitespace from text content
    const textContent = remainingContent.replace(/\n{3,}/g, '\n\n').trim();

    return { textContent, codeContent };
  }, [content, isStreaming]);

  return (
    <div>
      {/* Code section - collapsible */}
      {codeContent && (
        <CollapsibleCodeBlock codeContent={codeContent} isStreaming={isStreaming} />
      )}

      {/* Text, visualizations, and download links - disabled during streaming */}
      {textContent && (
        <div style={isStreaming ? { pointerEvents: 'none' } : undefined} className={isStreaming ? 'opacity-70' : ''}>
          <MessageContentRenderer
            content={textContent}
            isStreaming={isStreaming}
            debugInfo={debugInfo}
            onContentLoaded={onContentLoaded}
            translationData={translationData}
            modelId={modelId}
          />
        </div>
      )}
    </div>
  );
};

export default DataAnalysisResponse;
